//TO DO: in the next PR refactor and test this context
import {
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useIntl } from 'react-intl';
import { v4 as createUUID } from 'uuid';

import { CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import {
	trackRecaptchaFailure,
	trackSiteActivationViewed,
	trackCrossFlowActivationPlanFetched,
	trackCrossFlowActivationPlanFailed,
	trackSignupStarted,
	trackSiteNameChangedFromRecommendedSiteName,
	trackSitePickerSubmit,
	trackSubmitSuccess,
} from '@/components/signup/utils/analytics';
import { getConsents } from '@/components/signup/utils/get-consents';
import { getSignupContext } from '@/components/signup/utils/get-signup-context';
import { isTestTenantByName } from '@/components/signup/utils/is-test-tenant-by-name';
import {
	ProductKey,
	mapProductKeyAndEditionToCcpProduct,
} from '@/components/signup/utils/products';
import { getRecaptcha } from '@/components/signup/utils/recaptcha';
import { redirectToAdminHubForShadowIt } from '@/components/signup/utils/redirect-to-admin-hub-for-shadow-it.ts';
import {
	getAdminPaymentLink,
	type PaymentDetailsView,
} from '@/components/signup/utils/redirect-to-admin-hub-payment-details';
import {
	operateWithSignupUFOLogging,
	trackAndCompleteUFOEvent,
	SignupGetUFOErrorType,
	SignupUFOLoggingActionTimeoutError,
} from '@/components/signup/utils/signup-ufo-deprecated';
import {
	ufoEventExistingSiteN2ESubmit,
	ufoEventGraphQLWebhookSetup,
	ufoEventNewSiteN2NSubmit,
} from '@/components/signup/utils/ufo-events';
import {
	UfoEventName,
	UfoExperienceStatus,
	UfoInteractionType,
	type UfoLabelStack,
} from '@/components/signup/utils/ufo.types';
import { getCrossFlowActivationUrl } from '@/components/signup/utils/url';
import {
	getAdminHubUpgradeProductUrl,
	getConfirmationPageUrl,
} from '@/components/signup/utils/url';
import {
	COHORTS,
	useCohort,
	useCrossflowEligibleSites,
	useUpgradableSites,
} from '@/components/user-context/helpers/cohorts';
import { useUserContext } from '@/components/user-context/user-context-provider';
import {
	IBxpWebStargateClient,
	useBxpWebStargateClient,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	ActivateProductCcpResponse,
	ExistingSite,
	JobName,
	Platform,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Cookie } from '@/utils/cookie';
import { useLocation } from '@/utils/location-provider';

import { ErrorType, useSiteNameValidation } from '../../hooks/use-site-name-validation';
import { type FormattedMessageProps, messages as validationMessages } from '../messages';

const { ABORTED, SUCCEEDED } = UfoExperienceStatus;
const { PRESS, TRANSITION } = UfoInteractionType;

// Persists the provisioningRequestId once the deferred-site-name (spark-defer-
// site-name-v2, TWCG-2407) flow starts provisioning, so that navigating back to
// signup does not re-mount the variant or re-trigger provisioning. Stored in
// sessionStorage when available, mirrored to a self-expiring cookie for users
// without sessionStorage (e.g. private mode). A TTL bounds how long a returning
// user is excluded, so a genuinely new signup later in the same session is not
// permanently suppressed.
const PROVISIONING_REQUEST_ID_KEY = 'sparkDeferSiteNameV2ProvisioningRequestId';
const PROVISIONING_REQUEST_ID_TTL_MS = 20 * 60 * 1000;
const PROVISIONING_REQUEST_ID_TTL_SECONDS = PROVISIONING_REQUEST_ID_TTL_MS / 1000;

// Stored as `${id}::${savedAtMs}` so reads can enforce the TTL.
const isStoredValueRecent = (value: string | null): boolean => {
	if (!value) {
		return false;
	}
	const [, savedAt] = value.split('::');
	const savedAtMs = Number(savedAt);
	if (!savedAtMs) {
		return false;
	}
	return Date.now() - savedAtMs <= PROVISIONING_REQUEST_ID_TTL_MS;
};

// Unlike sessionStorage or cookies, window.name is scoped to this tab — it
// survives reloads/back-forward navigation within the tab, but a different
// tab (even to the same page) starts with its own, unset window.name. Used
// to tag the cookie fallback below so it can still be recognised as
// tab-isolated even when sessionStorage itself is unavailable.
const TAB_ID_WINDOW_NAME_PREFIX = 'sparkDeferSiteNameV2TabId::';
const getTabId = (): string => {
	if (window.name.startsWith(TAB_ID_WINDOW_NAME_PREFIX)) {
		return window.name.slice(TAB_ID_WINDOW_NAME_PREFIX.length);
	}
	const tabId =
		typeof crypto !== 'undefined' && crypto.randomUUID
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random()}`;
	window.name = `${TAB_ID_WINDOW_NAME_PREFIX}${tabId}`;
	return tabId;
};

export const saveProvisioningRequestId = (provisioningRequestId: string) => {
	if (typeof window === 'undefined' || !provisioningRequestId) {
		return;
	}
	const value = `${provisioningRequestId}::${Date.now()}`;
	// Prefer sessionStorage: it is tab-isolated, so activating in one tab does not
	// affect a separate signup in another tab. Only fall back to a cookie when
	// sessionStorage is unavailable (e.g. Safari private mode).
	let sessionStorageAvailable = false;
	try {
		window.sessionStorage.setItem(PROVISIONING_REQUEST_ID_KEY, value);
		sessionStorageAvailable = true;
	} catch {
		// Ignore; the cookie fallback below covers storage-unavailable browsers.
	}
	if (!sessionStorageAvailable) {
		// Cookies aren't tab-isolated on their own, so tag the value with this
		// tab's id — a different tab's read will see the cookie but recognise
		// it belongs to another tab and ignore it (see hasStoredProvisioningRequestId).
		Cookie.writeCookieInSeconds(
			PROVISIONING_REQUEST_ID_KEY,
			`${getTabId()}::${value}`,
			PROVISIONING_REQUEST_ID_TTL_SECONDS,
		);
	}
};

export const hasStoredProvisioningRequestId = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	let stored: string | null = null;
	try {
		stored = window.sessionStorage.getItem(PROVISIONING_REQUEST_ID_KEY);
	} catch {
		stored = null;
	}
	if (isStoredValueRecent(stored)) {
		return true;
	}
	// Fall back to the cookie when sessionStorage is empty or unavailable, but
	// only if it was written by this same tab.
	const cookieValue = Cookie.readCookie(PROVISIONING_REQUEST_ID_KEY);
	if (!cookieValue) {
		return false;
	}
	const [tabId, ...rest] = cookieValue.split('::');
	if (tabId !== getTabId()) {
		return false;
	}
	return isStoredValueRecent(rest.join('::'));
};

// spark-defer-site-name-v2 (TWCG-2407): when the auto-activation attempt
// fails, the variant falls back to a screen that mounts a brand-new
// SiteActivation instance with its own fresh provider — that instance has no
// memory of the error the failing attempt already surfaced. Carrying just the
// error/trace id (a plain string) through sessionStorage lets the new
// instance rebuild the same message from its own copy of the message
// template, without needing to serialize the message object itself (its
// `values` include a render function, which sessionStorage can't hold).
// "Take" semantics (read-then-clear) mean a stale id can never leak into an
// unrelated later mount.
const AUTO_ACTIVATION_FAILURE_ERROR_ID_KEY = 'sparkDeferSiteNameV2AutoActivationFailureErrorId';

export const saveAutoActivationFailureErrorId = (errorId: string) => {
	try {
		window.sessionStorage.setItem(AUTO_ACTIVATION_FAILURE_ERROR_ID_KEY, errorId);
	} catch {
		// Best-effort only: if sessionStorage is unavailable, the fallback screen
		// simply won't have the specific error id to show.
	}
};

export const takeAutoActivationFailureErrorId = (): string | undefined => {
	try {
		const errorId = window.sessionStorage.getItem(AUTO_ACTIVATION_FAILURE_ERROR_ID_KEY);
		if (errorId) {
			window.sessionStorage.removeItem(AUTO_ACTIVATION_FAILURE_ERROR_ID_KEY);
		}
		return errorId ?? undefined;
	} catch {
		return undefined;
	}
};

const ufoBaseLogs: UfoLabelStack = [
	{ name: 'pkg:site-activation' },
	{ name: 'component:siteActivationContextProvider' },
];
export interface SiteActivationContext {
	activateSite: ({
		privacyPolicy,
		skipManualFormValidationFlag,
	}: {
		privacyPolicy?: string;
		skipManualFormValidationFlag?: boolean;
	}) => void;
	sites: ExistingSite[];
	isRequestInProgress: boolean;
	showSitePicker: boolean;
	email?: string;
	sitePickerValue: ExistingSite | undefined;
	setSitePickerValue: Dispatch<ExistingSite | undefined>;
	setSiteNameValid: Dispatch<SetStateAction<boolean>>;
	siteName: string;
	setSiteNameValue: Dispatch<SetStateAction<string>>;
	isSubmitting: boolean;
	hasSubmitted: boolean;
	setHasSubmitted: Dispatch<SetStateAction<boolean>>;
	validationMessageState: [
		FormattedMessageProps | undefined,
		Dispatch<SetStateAction<FormattedMessageProps | undefined>>,
	];
	setShowSitePicker: Dispatch<SetStateAction<boolean>>;
	recommendedSiteName: string;
	isValid?: boolean;
	isValidating: boolean;
	error: ErrorType;
	isWorkspaceProduct: boolean;
	selectedTransactionAccountId: string;
	setSelectedTransactionAccountId: Dispatch<SetStateAction<string>>;
	primaryCollectionProduct?: string;
	/** "payment" shows payment overlay; view "payment-required" includes adminPaymentLink (opens in new tab); "site-setup" shows form. */
	paymentDetailsView: PaymentDetailsView;
	setPaymentDetailsView: Dispatch<SetStateAction<PaymentDetailsView>>;
	/**
	 * True when the spark-defer-site-name-v2 auto-activation path aborted because
	 * the recommended name failed its last-moment availability re-check. Consumers
	 * use this to fall back to the manual naming screen (TWCG-2407).
	 */
	autoActivationFailed: boolean;
}

export const SiteActivationContext = createContext<SiteActivationContext | null>(null);

export const useSiteActivationContext = (): SiteActivationContext => {
	const context = useContext(SiteActivationContext);
	if (!context) {
		throw new Error('useSiteActivationContext must be used within a SiteActivationContextProvider');
	}
	return context;
};

async function getHasPaidTargetEditionRequiringConfirmationScreen({
	cloudId,
	productKey,
	stargateClient,
	signupSource,
	createAnalyticsEvent,
}: {
	cloudId?: string;
	productKey?: ProductKey;
	signupSource: string | null;
	stargateClient: IBxpWebStargateClient;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}): Promise<boolean> {
	if (
		!cloudId ||
		!productKey ||
		signupSource === 'pricing' ||
		![
			ProductKey.CONFLUENCE,
			ProductKey.JIRA_SOFTWARE,
			ProductKey.JIRA_PRODUCT_DISCOVERY,
			ProductKey.JIRA_SERVICE_DESK,
			ProductKey.SERVICE_COLLECTION,
		].includes(productKey)
	)
		return false;

	try {
		trackCrossFlowActivationPlanFetched({
			createAnalyticsEvent,
			cloudId,
			productKey,
		});
		const activateProductCrossflowPlanResult = await stargateClient.activateProductCrossflowPlan({
			cloudId,
			productKey,
			jobName: JobName.WAC_SIGNUP,
			context: {
				sourceComponent: 'redirect',
				sourceContext: 'start',
				originProduct: 'wac',
			},
		});

		const activatePlanTaskContext = activateProductCrossflowPlanResult?.tasks?.find(
			(task) => task?.name === 'activate',
		)?.context;
		const plannedEditionId = (
			activatePlanTaskContext?.['edition'] as {
				id?: string;
			}
		)?.['id'];
		const plannedEditionStrategy = activatePlanTaskContext?.['editionStrategy'];

		return (
			(plannedEditionStrategy === 'highest' && plannedEditionId !== 'free') ||
			(plannedEditionStrategy === 'servco' && plannedEditionId === 'standard')
		);
	} catch (error) {
		trackCrossFlowActivationPlanFailed({
			createAnalyticsEvent,
			cloudId,
			productKey,
			error,
		});
		return false;
	}
}

export const SiteActivationContextProvider = ({
	children,
	existingSites,
	useContactExpandCohortLogic,
	autoActivateWithRecommendedName = false,
}: {
	children: ReactNode;
	existingSites?: ExistingSite[];
	useContactExpandCohortLogic?: boolean;
	/**
	 * When true, the provider fetches a recommended site name, sets it as the
	 * site name, and automatically activates the site once the name validates —
	 * skipping the manual naming step. Used by the spark-defer-site-name-v2
	 * experiment (TWCG-2407). Defaults to false so existing cohorts are
	 * unaffected.
	 */
	autoActivateWithRecommendedName?: boolean;
}) => {
	//HOOKS
	const intl = useIntl();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const {
		profile,
		user,
		product,
		edition: originalEdition = 'free',
		transactionAccounts,
	} = useUserContext();

	/**
	 * Rovo Dev (aka DEVAI) is a special case, it only has a single
	 * edition, standard. There is an outstanding DACI:
	 * https://hello.atlassian.net/wiki/spaces/uts/pages/5807772710/Rovo+Dev+Everywhere+SKU+DACI
	 * If/when this DACI is resolved and Rovo Dev supports the free
	 * edition, this special case can then be removed.
	 */
	let edition = originalEdition;
	if (product?.key === ProductKey.DEVAI) {
		edition = 'standard';
	}

	const { companyType, domainType, userType } = profile?.cohorts || {};
	const cohort = useCohort(useContactExpandCohortLogic);
	const expandableSites = useCrossflowEligibleSites();
	const upgradableSites = useUpgradableSites();
	const stargateClient = useBxpWebStargateClient();
	const location = useLocation();
	const localizedContactUrl = useLocalizedHref('/company/contact');
	const sites = useMemo(
		() =>
			existingSites ??
			(cohort === COHORTS.UPGRADE_ELIGIBLE_USER ? upgradableSites : expandableSites) ??
			[],
		[cohort, expandableSites, upgradableSites, existingSites],
	);
	const isWorkspaceProduct = useMemo(
		() =>
			!!product?.key &&
			[
				ProductKey.CONFLUENCE,
				ProductKey.TEAMWORK_COLLECTION,
				ProductKey.SERVICE_COLLECTION,
			].includes(product?.key),
		[product?.key],
	);

	//STATE
	const [showSitePicker, setShowSitePicker] = useState(sites.length > 0);
	const [siteNameValid, setSiteNameValid] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [sitePickerValue, setSitePickerValue] = useState<ExistingSite | undefined>(sites[0]);
	const [siteName, setSiteNameValue] = useState('');
	const [recommendedSiteName] = useState('');
	// Guards single-fire auto activation across re-renders/remounts, used by the
	// autoActivateWithRecommendedName flow (spark-defer-site-name-v2, TWCG-2407).
	const hasAutoActivatedRef = useRef(false);
	// If a previous (now-unmounted) auto-activation attempt in this tab left an
	// error id behind, this is the fresh SiteActivation instance the TWCG-2407
	// fallback screen mounts — seed the manual form with that same error rather
	// than showing it with no explanation. See saveAutoActivationFailureErrorId.
	const [initialAutoActivationFailureErrorId] = useState(() => takeAutoActivationFailureErrorId());
	// Set when auto-activation aborts because the recommended name's last-moment
	// availability re-check failed; consumers fall back to manual naming. Seeded
	// true when an error id above was found — SiteNameInput's own guard against
	// clearing that seeded message keys off this same flag (site-name-input.tsx).
	const [autoActivationFailed, setAutoActivationFailed] = useState(
		!!initialAutoActivationFailureErrorId,
	);
	// Tracks whether the in-flight activateSiteHandler call was started by the
	// auto-activation path (skipManualFormValidationFlag), so the recaptcha-retry
	// effect below — which calls activateSiteHandler directly, bypassing
	// activateSite's own closure over that flag — can still flag
	// autoActivationFailed on failure (TWCG-2407).
	const isAutoActivationAttemptRef = useRef(false);
	const [selectedTransactionAccountId, setSelectedTransactionAccountId] = useState<string>(
		transactionAccounts[0]?.id ?? '',
	);
	const [recaptchaFailedToLoad, setRecaptchFailedToLoad] = useState<boolean>(false);
	const [recaptchaRetryTimeRemainingSecond, setRecaptchaRetryTimeRemainingSecond] = useState(60);
	const [validatationMessage, setValidationMessage] = useState<undefined | FormattedMessageProps>(
		() =>
			initialAutoActivationFailureErrorId
				? {
						...validationMessages.timeout,
						values: {
							// eslint-disable-next-line react/no-unstable-nested-components
							'href-support': (node: ReactNode[]) => <a href={localizedContactUrl}>{node}</a>,
							error_id: initialAutoActivationFailureErrorId,
						},
					}
				: undefined,
	);
	const [paymentDetailsView, setPaymentDetailsView] = useState<PaymentDetailsView>({
		view: 'site-setup',
	});

	//VARIABLES
	const searchParameters = new URLSearchParams(location.search);
	const platform = (searchParameters.get('platform') ?? 'routing') as Platform;
	const primaryCollectionProduct = searchParameters.get('primaryCollectionProduct') as
		| ProductKey
		| undefined;
	const isNewUser = profile.cohorts?.userType === 'new-user';
	const productKey = product?.key as ProductKey;
	const { isValid, isValidating, error } = useSiteNameValidation(siteName, setSiteNameValid);
	const isEmbedded = !location.pathname?.endsWith('/try/cloud/signup');
	const dataFoundationsActionSubjectId = isEmbedded ? 'embedded' : 'standalone';

	const showRecaptchaFormError = useCallback(() => {
		const anonymousId = decodeURIComponent(Cookie.readCookie('ajs_anonymous_id') || 'n/a');
		setValidationMessage({
			...validationMessages.timeout,
			values: {
				// eslint-disable-next-line react/no-unstable-nested-components
				'href-support': (node: ReactNode[]) => <a href={localizedContactUrl}>{node}</a>,
				error_id: `Anonymous ID: ${anonymousId}`,
			},
		});

		// Reset form to allow re-submit
		setIsSubmitting(false);
		setHasSubmitted(false);
	}, [localizedContactUrl]);

	const activateSiteHandler = useCallback(
		async (signupId?: string, privacyPolicy?: string) => {
			setIsSubmitting(true);

			if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
				// set trigger that will wait for Recaptcha retry logic
				setRecaptchFailedToLoad(true);
				return;
			}
			setRecaptchFailedToLoad(false);

			const localeData = await stargateClient.getConsentConfig();

			const testingPurposes = isTestTenantByName(siteName);
			const signupContext = getSignupContext({
				productKey,
				testingPurposes,
				signupId,
				search: location.search,
				locale: intl.locale,
			});

			const MAX_SIGNUP_CONTEXT_LENGTH = 8192;
			let signupContextString: string = JSON.stringify(signupContext);
			if (signupContextString.length > MAX_SIGNUP_CONTEXT_LENGTH) {
				signupContextString = '{}';
			}
			const ccpProducts = mapProductKeyAndEditionToCcpProduct({
				productKey,
				edition,
				primaryCollectionProduct,
			});

			const gRecaptchaResponse = await getRecaptcha(user['email'] || '', executeRecaptcha);

			ufoEventNewSiteN2NSubmit.setCustomData({
				gRecaptchaResponse: gRecaptchaResponse ?? 'none',
				executeRecaptchaExists: Boolean(executeRecaptcha),
				reactComponent: 'SiteActivationProvider',
			});

			// Handle missing reCAPTCHA response
			if (!gRecaptchaResponse) {
				trackRecaptchaFailure(createAnalyticsEvent, 'site-activation');
				// spark-defer-site-name-v2 (TWCG-2407): the auto-activation path has no
				// visible form to surface showRecaptchaFormError's message on. Without
				// this, a recaptcha that never loads leaves the user on the spinner
				// forever — fall back to manual naming instead.
				if (isAutoActivationAttemptRef.current) {
					setAutoActivationFailed(true);
					setIsSubmitting(false);
					setHasSubmitted(false);
				} else {
					showRecaptchaFormError();
				}
				return;
			}

			// Set experiment metadata before starting UFO event for GraphQL webhook setup
			ufoEventGraphQLWebhookSetup.setCustomData({
				webPlatform: 'site-activation',
			});

			// get consents
			const consent = getConsents(
				localeData.locale ?? 'US-VA',
				localeData.localeRequiresMarketingCommunicationOptIn,
				privacyPolicy,
			);

			const response = await stargateClient.activateProduct({
				displayName: user['name'],
				email: user['email'] || '',
				ccpProducts: [ccpProducts],
				siteName,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				useCcpPlatform: platform === 'ccp',
				consent,
				platform,
				signupContext: signupContextString,
				isNewUser: productKey === ProductKey.JIRA_SOFTWARE || isNewUser,
				gRecaptchaResponse,
				testingPurposes,
				signupId,
				transactionAccountId: selectedTransactionAccountId,
			});

			// spark-defer-site-name-v2 (TWCG-2407): persist the provisioning request
			// id (the same value appended to the confirmation URL) before redirecting
			// so that if the user navigates back to signup we can recognise that
			// provisioning already started — the auto-activation effect and the
			// experiment's screen branch both check this to avoid re-provisioning.
			// Gated on autoActivateWithRecommendedName so this only runs for the
			// deferred-site-name variant; control and all other cohorts never store
			// an id, keeping their behaviour unchanged.
			if (autoActivateWithRecommendedName) {
				const provisioningRequestId = (response as ActivateProductCcpResponse).metadata
					?.provisionRequestId;
				if (provisioningRequestId) {
					saveProvisioningRequestId(provisioningRequestId);
				}
			}

			const redirectUrl = getConfirmationPageUrl({
				locale: intl.locale,
				productKey,
				response,
				siteName,
				signupId,
				edition,
				primaryCollectionProduct,
			});
			window.location.assign(redirectUrl);
		},
		[
			executeRecaptcha,
			recaptchaRetryTimeRemainingSecond,
			showRecaptchaFormError,
			stargateClient,
			siteName,
			primaryCollectionProduct,
			location.search,
			productKey,
			edition,
			user,
			platform,
			isNewUser,
			intl.locale,
			createAnalyticsEvent,
			selectedTransactionAccountId,
			autoActivateWithRecommendedName,
		],
	);

	useEffect(() => {
		if (profile.requestInProgress || !analyticsClient) return;
		trackSiteActivationViewed(createAnalyticsEvent, {
			actionSubjectId: dataFoundationsActionSubjectId,
			cohort,
			productKey,
			edition,
			userType,
			domainType,
			companyType,
			numTxas: transactionAccounts.length,
		});
	}, [
		analyticsClient,
		cohort,
		createAnalyticsEvent,
		companyType,
		dataFoundationsActionSubjectId,
		domainType,
		edition,
		productKey,
		profile.requestInProgress,
		userType,
		transactionAccounts.length,
	]);

	useEffect(() => {
		const ufoSubmitEvent = showSitePicker
			? ufoEventExistingSiteN2ESubmit
			: ufoEventNewSiteN2NSubmit;

		if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
			ufoSubmitEvent.failure({
				customData: {
					reactComponent: 'SiteActivationContextProvider',
					analyticsEvent: 'errored',
					recaptchaRetryTimeRemainingSecond: 0,
					siteType: showSitePicker ? 'existing-site' : 'new-site',
				},
				error: new Error('Recaptcha retry failed'),
			});
		}
		if (recaptchaFailedToLoad) {
			activateSiteHandler()
				.then(() => {
					ufoSubmitEvent.success({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'succeeded',
							siteType: showSitePicker ? 'existing-site' : 'new-site',
							recaptchaRetryTimeRemainingSecond,
						},
					});
				})
				.catch((error) => {
					// spark-defer-site-name-v2 (TWCG-2407): if the retried call belongs
					// to the auto-activation path, fall back to manual naming instead of
					// (also) surfacing showRecaptchaFormError's message on a form that
					// path never renders.
					if (isAutoActivationAttemptRef.current) {
						setAutoActivationFailed(true);
						setIsSubmitting(false);
						setHasSubmitted(false);
					} else {
						showRecaptchaFormError();
					}
					ufoSubmitEvent.failure({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'errored',
							siteType: showSitePicker ? 'existing-site' : 'new-site',
							recaptchaRetryTimeRemainingSecond,
						},
						error,
					});
					return;
				});
		}
	}, [
		activateSiteHandler,
		executeRecaptcha,
		localizedContactUrl,
		recaptchaFailedToLoad,
		recaptchaRetryTimeRemainingSecond,
		showRecaptchaFormError,
		showSitePicker,
	]);

	useEffect(() => {
		if (!recaptchaFailedToLoad) return;
		if (recaptchaRetryTimeRemainingSecond <= 0) return;
		const intervalId = setInterval(() => {
			setRecaptchaRetryTimeRemainingSecond(recaptchaRetryTimeRemainingSecond - 1);
		}, 1000);
		return () => clearInterval(intervalId);
	}, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

	const activateSite = async ({
		privacyPolicy,
		skipManualFormValidationFlag = false,
	}: {
		privacyPolicy?: string;
		/**
		 * When true, bypass the manual naming form's `siteNameValid` gate (that
		 * flag is only set by the form, which the auto-activation flow does not
		 * render). This does NOT skip the site-name availability check — the
		 * auto-activation flow performs an authoritative availability re-check
		 * below before activating. Used by spark-defer-site-name-v2 (TWCG-2407).
		 */
		skipManualFormValidationFlag?: boolean;
		// eslint-disable-next-line complexity
	}) => {
		setHasSubmitted(true);
		sessionStorage.setItem('provisioningStartTime', `${Date.now()}`);
		// Start UFOs to begin duration metrics for signup submit
		ufoEventNewSiteN2NSubmit.start({
			productKey,
			webPlatform: 'site-activation',
		});
		ufoEventExistingSiteN2ESubmit.start({
			productKey,
			webPlatform: 'site-activation',
		});
		const ufoLogs: UfoLabelStack = [...ufoBaseLogs, { name: 'method:activateSite' }];

		// user must be logged in
		if (!user.isUserLoggedIn) return;

		// Generate a signup ID and add it to all the events in this flow.
		// This id is used to stitch together a view of the end-to-end
		// provisioning experience, all the way to landing in product.
		const signupId = createUUID();
		const isN2N = !showSitePicker && !sitePickerValue;
		const domainType = profile?.cohorts?.domainType;
		trackSignupStarted(createAnalyticsEvent, signupId, productKey, edition, isN2N, domainType);

		// handle site picker submit
		if (showSitePicker) {
			if (sitePickerValue) {
				const upgradableProduct = sitePickerValue.products.find(
					(product) =>
						[product.productKey, product.partOfCollection].includes(productKey) &&
						product.productEdition === 'free',
				);

				// checking the standard-to-premium upgrade eligibility cohort
				const hasUpgradableStandardProduct =
					edition === 'premium' &&
					[
						ProductKey.JIRA_SOFTWARE,
						ProductKey.CONFLUENCE,
						ProductKey.JIRA_PRODUCT_DISCOVERY,
					].includes(productKey) &&
					sitePickerValue.products.some(
						(product) =>
							[product.productKey].includes(productKey) && product.productEdition === 'standard',
					);

				if (upgradableProduct || hasUpgradableStandardProduct) {
					// send to adminhub
					const redirectUrl = await getAdminHubUpgradeProductUrl(
						sitePickerValue,
						productKey,
						edition,
					);

					createAnalyticsEvent({
						eventName: 'signupUpgrade',
						eventComponent: 'button',
						billingSystem: sitePickerValue.products[0]?.billingSourceSystem,
						cloudId: sitePickerValue.cloudId,
						event: 'clicked',
						eventType: 'ui',
						action: 'upgrade',
					}).fire('signup');

					window.location.assign(redirectUrl);
					return;
				}

				try {
					// Check for Shadow IT authorization.
					await stargateClient.getActivationAuthorization({
						productKeys: [productKey],
						productEdition: edition as string,
						cloudId: sitePickerValue.cloudId,
						siteName: sitePickerValue?.displayName,
					});

					const searchParameters = new URLSearchParams(window.location.search);
					const signupSource = searchParameters.get('signupSource');
					// Fetch the activation plan once the user has clicked so redirect
					// handling can preserve paid-target confirmation flows.
					const hasPaidTargetEditionRequiringConfirmationScreen =
						await getHasPaidTargetEditionRequiringConfirmationScreen({
							cloudId: sitePickerValue.cloudId,
							productKey,
							stargateClient,
							signupSource,
							createAnalyticsEvent,
						});

					const crossFlowActivationUrl = getCrossFlowActivationUrl({
						cloudId: sitePickerValue.cloudId,
						edition,
						productKey: primaryCollectionProduct ?? productKey,
						siteName: sitePickerValue?.displayName,
						hasPaidTargetEditionRequiringConfirmationScreen,
					});

					ufoEventExistingSiteN2ESubmit.success({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'succeeded',
							siteType: 'existing-site',
						},
					});

					trackSitePickerSubmit({
						createAnalyticsEvent,
						targetCloudId: sitePickerValue.cloudId,
						provisionedProducts: [{ product: productKey, edition }],
						signupUrl: crossFlowActivationUrl,
						aaid: user['accountId'],
						signupSource,
						componentSource: 'SiteActivationContextProvider',
						hasPaidTargetEditionRequiringConfirmationScreen,
					});

					window.location.assign(crossFlowActivationUrl);
				} catch (error) {
					ufoEventExistingSiteN2ESubmit.failure({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'errored',
							siteType: 'existing-site',
						},
						error,
					});

					redirectToAdminHubForShadowIt(error);
				}
			}
			return;
		}

		if (isValidating) {
			const { availability } = await stargateClient.checkCloudNameAvailability({
				cloudName: siteName,
			});

			if (availability !== 'AVAILABLE') {
				setHasSubmitted(false); // reset to allow re-submit
				return;
			}

			setSiteNameValid(true);
		}

		// spark-defer-site-name-v2 (TWCG-2407): the auto-activation path bypasses
		// the manual-form siteNameValid gate, but the recommended name must still
		// be confirmed available immediately before activating — it may have been
		// taken by another signup since the validation hook last ran. Re-check
		// authoritatively and, if it is not available, abort and let the consumer
		// fall back to the manual naming screen rather than activating a bad name.
		if (skipManualFormValidationFlag) {
			const availabilityResult = await stargateClient
				.checkCloudNameAvailability({ cloudName: siteName })
				.catch(() => undefined);

			// Fail safe: treat a missing/errored/non-available result as not
			// available so we never activate a name we could not confirm.
			if (availabilityResult?.availability !== 'AVAILABLE') {
				setAutoActivationFailed(true);
				setHasSubmitted(false); // reset to allow re-submit
				return;
			}
		}

		if ((!siteNameValid && !isValidating && !skipManualFormValidationFlag) || !siteName) {
			setHasSubmitted(false); // reset to allow re-submit
			return;
		}

		trackSubmitSuccess(createAnalyticsEvent);

		if (siteName !== recommendedSiteName) {
			trackSiteNameChangedFromRecommendedSiteName(
				createAnalyticsEvent,
				siteName,
				recommendedSiteName,
			);
		}

		// Record whether this attempt is the auto-activation one so a later
		// recaptcha-retry (which calls activateSiteHandler directly, outside this
		// closure) can still recognise it on failure (TWCG-2407).
		isAutoActivationAttemptRef.current = skipManualFormValidationFlag;

		let setTimeoutId: ReturnType<typeof setTimeout>;

		const interactionId = createUUID();
		operateWithSignupUFOLogging(
			{
				interactionId,
				interactionName: 'try-cloud-signup_form-submit',
				type: 'press',
				timeout: 2 * 60 * 1000,
				labelStack: [{ name: 'uip' }],
				errorContext: 'error occured on signup form submit button',
			},
			async () =>
				Promise.race([
					new Promise((_resolve, reject) => {
						setTimeoutId = setTimeout(() => {
							reject(SignupUFOLoggingActionTimeoutError);
						}, 60000);
					}),
					activateSiteHandler(signupId, privacyPolicy).finally(() => {
						if (setTimeoutId) {
							clearTimeout(setTimeoutId);
						}
					}),
				]),
		)
			.then(() => {
				// SLO "good event" for N2N signup submit
				ufoEventNewSiteN2NSubmit.success({
					customData: {
						reactComponent: 'SiteActivationContextProvider',
						analyticsEvent: 'succeeded',
						siteType: 'new-site',
					},
				});

				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.ACTIVATE_PRODUCT_API_SUCCESS,
					experienceStatus: SUCCEEDED,
					interactionType: TRANSITION,
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "good event" for signup submit captured on bxp-ui only
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_SUCCEEDED,
					experienceStatus: SUCCEEDED,
					interactionType: PRESS,
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "good event" for signup submit captured across bxp-ui and bxpkit
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_SUCCEEDED,
					experienceStatus: SUCCEEDED,
					interactionType: PRESS,
					ufoLogger: ufoLogs,
				});
			})
			.catch((error) => {
				// Show add-payment-method page if Rovo Dev 403 (user clicks "Open payment methods" to open in new tab)
				if (productKey === ProductKey.DEVAI && error?.response?.status === 403) {
					const adminPaymentLink = getAdminPaymentLink(error);
					if (adminPaymentLink) {
						setIsSubmitting(false);
						setHasSubmitted(false);
						setPaymentDetailsView({
							view: 'payment-required',
							adminPaymentLink,
						});
						return;
					}
				}

				// SLO "bad event" for N2N signup submit
				ufoEventNewSiteN2NSubmit.failure({
					customData: {
						reactComponent: 'SiteActivationContextProvider',
						analyticsEvent: 'errored',
						siteType: 'new-site',
					},
					error,
				});

				redirectToAdminHubForShadowIt(error);

				const traceId = error?.response?.headers?.get('atl-traceid');
				const errorId = traceId ? `Trace ID: ${traceId}` : `Interaction ID: ${interactionId}`;
				setValidationMessage({
					...validationMessages.timeout,
					values: {
						// eslint-disable-next-line react/no-unstable-nested-components
						'href-support': (node: ReactNode[]) => <a href={localizedContactUrl}>{node}</a>,
						error_id: errorId,
					},
				});

				// spark-defer-site-name-v2 (TWCG-2407): the auto-activation path has
				// no visible form to surface this error on. Fall back to the manual
				// naming screen so the user sees the create-a-site panel (pre-filled
				// with the generated name via siteName) and the validation message
				// above, and can retry. Control is unaffected (flag is false there).
				// The message just set above lives on this (about to be replaced)
				// provider instance, so persist the error id for the fresh instance
				// the fallback mounts to pick back up on its own initial render.
				if (skipManualFormValidationFlag) {
					saveAutoActivationFailureErrorId(errorId);
					setAutoActivationFailed(true);
				}

				// don't re-enable the form when there is an HTTP exception. this helps with rate-limiting
				const errorType = SignupGetUFOErrorType(error);
				if (errorType !== 'http_exception') {
					// Reset form to allow re-submit
					setIsSubmitting(false);
					setHasSubmitted(false);
				}

				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.ACTIVATE_PRODUCT_API_ERROR,
					experienceStatus: ABORTED,
					interactionType: TRANSITION,
					error,
					errorName: 'ActivateProductError',
					errorMessage: 'error occured on activateProduct',
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "bad event" for signup submit captured on bxp-ui only
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_FAILED,
					experienceStatus: ABORTED,
					interactionType: PRESS,
					error,
					errorName: 'ActivateProductError',
					errorMessage: 'error occured on activateProduct',
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "bad event" for signup submit captured across bxp-ui and bxpkit
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_FAILED,
					experienceStatus: ABORTED,
					interactionType: PRESS,
					error,
					errorName: 'ActivateProductError',
					errorMessage: 'error occured on activateProduct',
					ufoLogger: ufoLogs,
				});
			});
	};

	// activateSite is a plain async function that gets a new reference every
	// render. Hold the latest version in a ref so the auto-activation effect can
	// call it without listing it as a dependency (which would otherwise re-run the
	// effect every render).
	const activateSiteRef = useRef(activateSite);
	activateSiteRef.current = activateSite;

	// spark-defer-site-name-v2 (TWCG-2407): when a consumer opts into
	// auto-activation, activate the site automatically once a (recommended) site
	// name has been populated and validated — skipping the manual naming step.
	// The recommended name itself is fetched by the consumer via
	// useRecommendedSiteName, which writes it into siteName. hasAutoActivatedRef
	// keeps this to a single invocation across re-renders/remounts.
	useEffect(() => {
		if (
			!autoActivateWithRecommendedName ||
			hasAutoActivatedRef.current ||
			showSitePicker ||
			!siteName ||
			isValidating ||
			// Provisioning already started in this session (e.g. the user navigated
			// back to signup after activation). Do not re-activate; the experiment's
			// screen branch also checks this so the existing-sites screen renders.
			hasStoredProvisioningRequestId()
		) {
			return;
		}
		if (!isValid) {
			// The recommended name failed validation (e.g. checkCloudNameAvailability
			// errored, or the name itself is invalid). That failure resolves without
			// ever throwing, so nothing else would flag this — without this check the
			// auto-activation path is stuck on the spinner forever since isValid can
			// never become true. Fall back to manual naming instead.
			// error's own "no error yet" sentinel (ErrorType.NO_ERROR) is a non-empty
			// string and therefore truthy — must compare against it explicitly, or
			// this fires on every transient not-yet-validated render and permanently
			// blocks activation (hasAutoActivatedRef.current) before isValid ever
			// gets a chance to become true.
			if (error && error !== ErrorType.NO_ERROR) {
				hasAutoActivatedRef.current = true;
				setAutoActivationFailed(true);
			}
			return;
		}
		hasAutoActivatedRef.current = true;
		// The recommended name has already passed the validation hook (isValid),
		// so skip activateSite's manual-form siteNameValid gate (that flag is only
		// set by the manual naming form, which we bypass here).
		activateSiteRef.current({ skipManualFormValidationFlag: true });
	}, [autoActivateWithRecommendedName, showSitePicker, siteName, isValidating, isValid, error]);

	return (
		<SiteActivationContext.Provider
			value={{
				isSubmitting,
				activateSite,
				showSitePicker,
				sites,
				isRequestInProgress: profile.requestInProgress,
				email: user['email'],
				sitePickerValue,
				setSitePickerValue,
				setSiteNameValid,
				siteName,
				setSiteNameValue,
				setShowSitePicker,
				recommendedSiteName,
				hasSubmitted,
				setHasSubmitted,
				validationMessageState: [validatationMessage, setValidationMessage],
				isValid,
				isValidating,
				error,
				isWorkspaceProduct,
				selectedTransactionAccountId,
				setSelectedTransactionAccountId,
				primaryCollectionProduct,
				paymentDetailsView,
				setPaymentDetailsView,
				autoActivationFailed,
			}}
		>
			{children}
		</SiteActivationContext.Provider>
	);
};
